import { filterDuplicates, getDefaultTrunkHeight } from '../DesignTool/utils';
import { alignPanelsToSegments } from '@aerialytic/designer-states/core';
import { resolveScopeFromDesignId } from '@aerialytic/designer-states/host';

export { alignPanelsToSegments };

export const normalizeTreeTrunkHeight = (trunkHeight) =>
  trunkHeight !== undefined && trunkHeight !== null
    ? Number(Number(trunkHeight).toFixed(2))
    : null;

const preferredCanvasRow = (items) => {
  const rows = Array.isArray(items) ? items : [];
  return (
    rows.find((item) => item?.is_default && !item?.archived_at) ??
    rows.find((item) => item?.is_default) ??
    rows.find((item) => !item?.archived_at) ??
    null
  );
};

const preferredCanvasId = (items) => {
  const preferred = preferredCanvasRow(items);
  const id = preferred?.id ?? preferred?.pk;
  return id ? String(id) : '';
};

const preferredDesignIdForCanvas = (canvas) => {
  if (!canvas) return '';
  if (canvas.default_design_id) return String(canvas.default_design_id);
  const designs = Array.isArray(canvas.designs) ? canvas.designs : [];
  const active = designs.filter((row) => !row?.archived_at);
  const preferred =
    active.find((row) => row?.is_default) ??
    active.find((row) => String(row?.id) === String(canvas.id || canvas.pk)) ??
    active[0];
  const id = preferred?.id ?? canvas.id ?? canvas.pk;
  return id ? String(id) : '';
};

const normalizeId = (value) =>
  typeof value === 'string' ? value.trim().replace(/\/+$/, '') : '';

export default (
  API,
  uid,
  designId,
  canvasesByLeadAPI,
  initialCanvasId,
  endpointFactory
) => {
  const explicitDesignId = normalizeId(designId);
  const explicitCanvasId = normalizeId(initialCanvasId);
  let canvasId = explicitCanvasId || explicitDesignId;
  let canvasDesignTargetId = explicitDesignId || explicitCanvasId || uid;
  let canvasTargetResolved = explicitCanvasId.length > 0;
  let explicitDesignSelection = explicitDesignId.length > 0;
  let manualPersistOnly = true;

  const buildRetrievePath = (resolvedCanvasId) => {
    const cId = normalizeId(String(resolvedCanvasId || canvasId || ''));
    const dId = normalizeId(String(canvasDesignTargetId || ''));
    const leadId = normalizeId(String(uid || ''));
    if (
      dId &&
      cId &&
      (explicitDesignSelection || (dId !== leadId && dId !== cId))
    ) {
      return `${encodeURIComponent(cId)}/?design_id=${encodeURIComponent(dId)}`;
    }
    return `${encodeURIComponent(cId)}/`;
  };

  const resolveCanvasId = async () => {
    if (canvasTargetResolved && canvasId) return canvasId;
    canvasTargetResolved = true;
    try {
      const data = canvasesByLeadAPI?.list
        ? await canvasesByLeadAPI.list(
            `?lead=${encodeURIComponent(uid)}${
              explicitDesignId ? '&include_archived=true' : ''
            }`
          )
        : null;
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : [];
      if (explicitDesignId) {
        const scope =
          (explicitCanvasId
            ? {
                canvasId: explicitCanvasId,
                designId: explicitDesignId
              }
            : null) || resolveScopeFromDesignId(items, explicitDesignId);
        if (scope) {
          canvasId = scope.canvasId;
          canvasDesignTargetId = scope.designId;
        } else {
          canvasId = explicitDesignId;
          canvasDesignTargetId = explicitDesignId;
        }
      } else {
        const preferred = preferredCanvasRow(items);
        canvasId = preferredCanvasId(items) || uid;
        canvasDesignTargetId =
          preferredDesignIdForCanvas(preferred) || canvasId;
      }
    } catch (e) {
      canvasId = canvasId || uid;
      canvasDesignTargetId = canvasDesignTargetId || canvasId;
    }
    return canvasId;
  };
  const dropKeys = (obj, keys) => {
    const next = { ...(obj || {}) };
    keys.forEach((k) => {
      if (k in next) delete next[k];
    });
    return next;
  };

  const sanitizeSegmentsForLegacy = (segments = []) =>
    (segments || []).map((seg) => {
      if (!seg || typeof seg !== 'object') return seg;
      const {
        annual_kwh,
        annualKwh,
        clipping_kwh,
        clippingKwh,
        monthly_clipping_kwh,
        monthlyClippingKwh,
        selected_panel_count,
        selectedPanelCount,
        stringing_data,
        stringingData,
        inverters_used,
        invertersUsed,
        dc_ac_ratio,
        dcAcRatio,
        ...rest
      } = seg;
      return rest;
    });

  const makeCanvasSaveError = (message, err, details = {}) => {
    const saveError = new Error(message);
    saveError.cause = err;
    saveError.response = err?.response;
    saveError.canvasSave = details;
    return saveError;
  };

  const logCanvasSaveError = (message, err, details = {}) => {
    console.warn('[CanvasAPI.saveCanvas]', message, {
      canvasId,
      status: err?.response?.status,
      response: err?.response?.data ?? err?.message,
      ...details,
    });
  };

  const parseJsonIfString = (value) => {
    if (value == null) return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      return value;
    }
  };

  const toNumOr = (value, fallback) => {
    if (value === undefined || value === null) return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const normalizeNumericList = (value, length, fallback) => {
    const source = Array.isArray(value) ? value : [];
    return Array.from({ length }, (_, index) =>
      toNumOr(source[index], fallback)
    );
  };
  const normalizeFixedList = (value, length, fallback) =>
    normalizeNumericList(value, length, fallback).map((val) =>
      val.toFixed(4)
    );
  const normalizeLines = (segment) => {
    const geometryLength = Array.isArray(segment?.geometry)
      ? segment.geometry.length
      : 0;
    const lines = Array.isArray(segment?.lines) ? segment.lines : [];
    const expectedLength = geometryLength || lines.length;
    return Array.from(
      { length: expectedLength },
      (_, index) => lines[index] || ''
    );
  };

  // helper to load data from API
  const loadData = (data) => {
    // console.log(data, 'Load');
    const shade = data.shaded;
    const colorRange = (data.color_range ? data.color_range : '0,2450')
      .split(',')
      .map(Number);
    const rawPanels = data.panels ?? data.Panels;
    const rawAnnotations = data.annotations ?? data.Annotations;
    const panels = Array.isArray(parseJsonIfString(rawPanels))
      ? parseJsonIfString(rawPanels)
      : [];
    const annotations = Array.isArray(parseJsonIfString(rawAnnotations))
      ? parseJsonIfString(rawAnnotations)
      : [];
    const selectedPanel = data.selected_panel || null;
    const selectedPanelId =
      data.selected_panel_id ??
      data.selectedPanelId ??
      (data.selected_panel && data.selected_panel.id) ??
      null;
    const selectedInverter = data.selected_inverter || null;
    const selectedInverterId =
      data.selected_inverter_id ??
      data.selectedInverterId ??
      (data.selected_inverter && data.selected_inverter.id) ??
      null;
    const selectedPanelSpec = data.selected_panel_spec || null;
    const selectedInverterSpec = data.selected_inverter_spec || null;
    const inverterQuantity = data.inverter_quantity;
    const primaryInverterAutoQuantity = data.primary_inverter_auto_quantity;
    const additionalInverters = data.additional_inverters || [];
    const losses = parseJsonIfString(data.losses ?? null);
    const useTargetProduction = Boolean(data.use_target_production);
    const targetProductionKwh = data.target_production_kwh ?? 0;
    const maxDcAcRatio = data.max_dc_ac_ratio ?? null;
    const onlySelectedPanels = Boolean(data.only_selected_panels);
    const weatherDataset = data.weather_dataset || null;
    const pitchInDegrees =
      data.pitch_in_degrees !== undefined && data.pitch_in_degrees !== null
        ? Boolean(data.pitch_in_degrees)
        : true;

    const totalAnnualKwh = data.total_annual_kwh || 0;
    const totalClippingKwh = data.total_clipping_kwh || 0;
    const dcAcRatio = data.dc_ac_ratio ?? null;
    const stringingData = data.stringing_data || null;
    const invertersUsed = data.inverters_used || null;
    const systemSizeKw = data.system_size_kw ?? null;
    const mpptCountTotal = data.mppt_count_total ?? null;
    const inverterCountTotal = data.inverter_count_total ?? null;
    const stringingPanelIndexMode =
      data.stringing_panel_index_mode || data.stringingPanelIndexMode || 'all';
    const productionPerPanel = data.production_per_panel ?? null;
    const productionPerString = data.production_per_string ?? null;
    const hourlyProduction = data.hourly_production ?? null;
    const shadingImages = data.shading_images ?? null;
    const droneProject = data.drone_project ?? null;
    const rgb = data.rgb ?? null;
    const dsm = data.dsm ?? null;
    const dsmParams = data.dsm_params ?? null;

    const parseMonthly = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        return val
          .split(',')
          .map((x) => (x === '' || x === null || x === undefined ? 0 : Number(x)));
      }
      return Array(12).fill(0);
    };
    const totalMonthlyKwh = parseMonthly(data.total_monthly_kwh);
    const totalMonthlyClippingKwh = parseMonthly(data.total_monthly_clipping_kwh);

    const buffer = toNumOr(
      data.buffer ?? data.Buffer,
      12.7
    );
    const rowSpacing = toNumOr(
      data.row_spacing ?? data.rowSpacing ?? data.RowSpacing,
      0
    );
    const panelTilt = toNumOr(
      data.panel_tilt ?? data.panelTilt ?? data.PanelTilt,
      0
    );
    const rawAzimuth = data.azimuth_restriction ?? data.azimuthRestriction ?? data.AzimuthRestriction;
    const azimuthRestriction = Array.isArray(rawAzimuth) && rawAzimuth.length === 2
      ? rawAzimuth.map(Number)
      : [0, 360];
    const panelPlacementFillMode = toNumOr(
      data.panel_placement_fill_mode ?? data.panelPlacementFillMode ?? data.PanelPlacementFillMode,
      3
    );
    const panelPlacementLayoutMode = toNumOr(
      data.panel_placement_layout_mode ?? data.panelPlacementLayoutMode ?? data.PanelPlacementLayoutMode,
      2
    );
    const panelPlacementOnlySelectedRoof = Boolean(
      data.panel_placement_only_selected_roof ?? data.panelPlacementOnlySelectedRoof ?? data.PanelPlacementOnlySelectedRoof
    );

    // get segments data
    const segments = [];
    (data.segments || [])
      .map(({
        lines,
        height,
        azimuth,
        panel_orientation: panelOrientation,
        panel_axis: panelAxis,
        ac_monthly: acMonthly,
        poa_monthly: poaMonthly,
        solar_access: solarAccess,
        ...segment
      }) => {
        const lineArray =
          Array.isArray(lines) && lines.length > 0
            ? lines
            : Array(segment.geometry.length).fill('');
        const acAnnual = acMonthly.reduce((a, b) => a + b, 0);
        const duplicates = filterDuplicates(segment.geometry);
        if (duplicates[0].length > 2) {
          segments.push({
            ...segment,
            shape: 'Polygon',
            height: +height,
            azimuth: +azimuth,
            lines: lineArray.map((lineLabel) => (lineLabel === 'None' ? '' : lineLabel)),
            panelOrientation,
            panelAxis,
            acMonthly,
            poaMonthly,
            acAnnual,
            solarAccess,
          });
        }
      });

    // get obstacle data
    const obstacles = (data.obstacles || []).map(({ radius, height, shape, ...obstacle }) => ({
      ...obstacle,
      height: +height,
      radius: shape === 'Polygon' ? null : +radius,
      shape,
    }));

    // get tree data
    const trees = (data.trees || []).map(({ radius, height, trunk_height, ...tree }) => ({
      ...tree,
      height: +height,
      trunkHeight: trunk_height !== undefined && trunk_height !== null
        ? +trunk_height
        : getDefaultTrunkHeight(+height),
    }));

    // add empty panel containers
    /* const panels = segments.map(({id, azimuth}) => {
          return {
            id,
            azimuth,
            horizontal: {height: 0, width: 0, points: []},
            vertical: {height: 0, width: 0, points: []},
            ac_monthly: [],
            solar_access: 1,
          };
        }) */

    // return all data
    return {
      canvasId: data.pk ?? data.id ?? data.canvas_id ?? null,
      segments,
      obstacles,
      trees,
      shade,
      shading_images: shadingImages,
      drone_project: droneProject,
      colorRange,
      panels: alignPanelsToSegments(panels, segments),
      annotations,
      weather_dataset: weatherDataset,
      selected_panel: selectedPanel,
      selected_panel_id: selectedPanelId,
      selected_inverter: selectedInverter,
      selected_inverter_id: selectedInverterId,
      selected_panel_spec: selectedPanelSpec,
      selected_inverter_spec: selectedInverterSpec,
      inverter_quantity: inverterQuantity,
      primary_inverter_auto_quantity: primaryInverterAutoQuantity,
      additional_inverters: additionalInverters,
      losses,
      total_annual_kwh: totalAnnualKwh,
      total_monthly_kwh: totalMonthlyKwh,
      total_clipping_kwh: totalClippingKwh,
      total_monthly_clipping_kwh: totalMonthlyClippingKwh,
      dc_ac_ratio: dcAcRatio,
      inverter_count_total: inverterCountTotal,
      stringing_panel_index_mode: stringingPanelIndexMode,
      stringing_data: stringingData,
      inverters_used: invertersUsed,
      production_per_panel: productionPerPanel,
      production_per_string: productionPerString,
      hourly_production: hourlyProduction,
      system_size_kw: systemSizeKw,
      mppt_count_total: mpptCountTotal,
      use_target_production: useTargetProduction,
      target_production_kwh: targetProductionKwh,
      max_dc_ac_ratio: maxDcAcRatio,
      only_selected_panels: onlySelectedPanels,
      pitch_in_degrees: pitchInDegrees,
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      panelPlacementFillMode,
      panelPlacementLayoutMode,
      panelPlacementOnlySelectedRoof,
      rgb,
      dsm,
      dsmParams,
      resolution: data.resolution,
    };
  };

  // recover data from previously saved canvas
  const getCanvas = async () => {
    await resolveCanvasId();
    return API.list(buildRetrievePath()).then((data) => {
      if (data?.design_id) {
        canvasDesignTargetId = normalizeId(String(data.design_id));
        explicitDesignSelection = true;
      }
      if (data?.canvas_id) {
        canvasId = normalizeId(String(data.canvas_id));
        canvasTargetResolved = true;
      }
      return loadData(data);
    });
  };

  // save all data
  const saveCanvas = async (canvasData, options = {}) => {
    if (manualPersistOnly && options?.force !== true) {
      return Promise.resolve(canvasData);
    }
    await resolveCanvasId();
    const strict = options?.strict === true;
    const actionId =
      typeof options?.actionId === 'string' ? options.actionId.trim() : '';
    const requestOptions = actionId
      ? { params: { panel_placement_action_id: actionId } }
      : {};
    const {
      segments, obstacles, trees, colorRange, ...canvas
    } = canvasData;
    if (segments) {
      canvas.segments = segments.map(({ acMonthly, poaMonthly, solarAccess, panelOrientation, panelAxis, tsrf, tof, ...segment }) => ({
        ...segment,
        lines: normalizeLines(segment),
        panel_orientation: panelOrientation,
        panel_axis: panelAxis ?? segment.panel_axis ?? null,
        ac_monthly: normalizeNumericList(acMonthly, 12, 0),
        poa_monthly: normalizeNumericList(poaMonthly, 12, 0),
        solar_access: normalizeFixedList(solarAccess, 13, 1),
        tsrf: normalizeFixedList(tsrf, 13, 1),
        tof: normalizeFixedList(tof, 13, 1),
      }));
    }
    if (obstacles) {
      canvas.obstacles = obstacles;
    }
    if (trees) {
      canvas.trees = trees.map(({ trunkHeight, ...tree }) => ({
        ...tree,
        trunk_height: normalizeTreeTrunkHeight(trunkHeight),
      }));
    }
    if (colorRange) canvas.color_range = colorRange.join(',');
    if (canvas.buffer !== undefined) canvas.buffer = canvas.buffer;
    if (canvas.rowSpacing !== undefined) {
      canvas.row_spacing = canvas.rowSpacing;
      delete canvas.rowSpacing;
    }
    if (canvas.panelTilt !== undefined) {
      canvas.panel_tilt = canvas.panelTilt;
      delete canvas.panelTilt;
    }
    if (canvas.azimuthRestriction !== undefined) {
      canvas.azimuth_restriction = canvas.azimuthRestriction;
      delete canvas.azimuthRestriction;
    }
    if (canvas.panelPlacementFillMode !== undefined) {
      canvas.panel_placement_fill_mode = canvas.panelPlacementFillMode;
      delete canvas.panelPlacementFillMode;
    }
    if (canvas.panelPlacementLayoutMode !== undefined) {
      canvas.panel_placement_layout_mode = canvas.panelPlacementLayoutMode;
      delete canvas.panelPlacementLayoutMode;
    }
    if (canvas.panelPlacementOnlySelectedRoof !== undefined) {
      canvas.panel_placement_only_selected_roof = canvas.panelPlacementOnlySelectedRoof;
      delete canvas.panelPlacementOnlySelectedRoof;
    }
    if (canvas.dsmParams !== undefined) {
      canvas.dsm_params = canvas.dsmParams;
      delete canvas.dsmParams;
    }
    if (canvasDesignTargetId) {
      canvas.design_id = canvasDesignTargetId;
    }
    ['total_monthly_kwh', 'total_monthly_clipping_kwh'].forEach((key) => {
      if (Array.isArray(canvas[key])) canvas[key] = canvas[key].join(',');
    });
    if (options?.canvasChanged === true) canvas.canvas_changed = true;
    if (options?.canvasChanged === false) canvas.canvas_changed = false;
    if (options?.confirmArchiveSiblings) canvas.confirm_archive_siblings = true;
    if (options?.updateDefaults) {
      canvas.update_defaults = true;
      canvas.update_lead_design_default = Boolean(options.updateLeadDesignDefault);
    }
    const legacyDrop = [
      'selected_panel_id',
      'selected_inverter_id',
      'selected_panel',
      'selected_inverter',
      'selected_panel_spec',
      'selected_inverter_spec',
      'inverter_quantity',
      'primary_inverter_auto_quantity',
      'additional_inverters',
      'losses',
      'total_annual_kwh',
      'total_monthly_kwh',
      'total_clipping_kwh',
      'total_monthly_clipping_kwh',
      'dc_ac_ratio',
      'system_size_kw',
      'mppt_count_total',
      'inverter_count_total',
      'stringing_data',
      'stringing_panel_index_mode',
      'inverters_used',
      'production_per_panel',
      'production_per_string',
      'hourly_production',
      'use_target_production',
      'target_production_kwh',
      'max_dc_ac_ratio',
      'only_selected_panels',
      'pitch_in_degrees',
      'weather_dataset'
    ];

    return API.update(canvasId, canvas, true, requestOptions)
      .then((data) => loadData(data))
      .catch((err) => {
        const status = err?.response?.status;
        const responseData = err?.response?.data;
        const detail = responseData?.detail && typeof responseData.detail === 'object'
          ? responseData.detail
          : responseData;
        if (status === 409 || detail?.code === 'canvas_sibling_archive_required') {
          const conflict = new Error('canvas_sibling_archive_required');
          conflict.status = 409;
          conflict.designs = detail?.designs || [];
          conflict.response = err.response;
          return Promise.reject(conflict);
        }
        logCanvasSaveError('primary update failed', err, {
          fields: Object.keys(canvas),
        });
        if (status !== 400 && status !== 404) {
          if (strict) {
            return Promise.reject(
              makeCanvasSaveError('Canvas save failed', err, {
                canvasId,
                fields: Object.keys(canvas),
              })
            );
          }
          return Promise.resolve(canvasData);
        }

        const droppedKeys = legacyDrop.filter((key) => key in canvas);
        const legacyCanvas = dropKeys(canvas, legacyDrop);
        if (legacyCanvas.segments) {
          legacyCanvas.segments = sanitizeSegmentsForLegacy(legacyCanvas.segments);
        }

        return API.update(canvasId, legacyCanvas, true, requestOptions)
          .then(
            (data) => {
              if (strict && droppedKeys.length > 0) {
                return Promise.reject(
                  makeCanvasSaveError(
                    'Canvas save used legacy fallback and omitted fields',
                    err,
                    {
                      canvasId,
                      droppedKeys,
                    }
                  )
                );
              }
              return loadData(data);
            },
            (fallbackErr) => {
              logCanvasSaveError('legacy fallback failed', fallbackErr, {
                fields: Object.keys(legacyCanvas),
                droppedKeys,
              });
              if (strict) {
                return Promise.reject(
                  makeCanvasSaveError('Canvas save fallback failed', fallbackErr, {
                    canvasId,
                    fields: Object.keys(legacyCanvas),
                    droppedKeys,
                  })
                );
              }
              return Promise.resolve(canvasData);
            }
          );
      });
  };

  const listCanvasDesignsForLead = async (options) => {
    const search = options?.includeArchived
      ? `?lead=${encodeURIComponent(uid)}&include_archived=true`
      : `?lead=${encodeURIComponent(uid)}`;
    const data = canvasesByLeadAPI?.list
      ? await canvasesByLeadAPI.list(search)
      : [];
    return Array.isArray(data) ? data : [];
  };

  const archiveCanvas = async (targetCanvasId) => {
    if (!endpointFactory) throw new Error('Archive is not available');
    const archiveAPI = endpointFactory(
      `designer/canvas/${encodeURIComponent(String(targetCanvasId))}/archive/`
    );
    return archiveAPI.create({});
  };

  const restoreCanvas = async (targetCanvasId) => {
    if (!endpointFactory) throw new Error('Restore is not available');
    const restoreAPI = endpointFactory(
      `designer/canvas/${encodeURIComponent(String(targetCanvasId))}/restore/`
    );
    return restoreAPI.create({});
  };

  const archiveDesign = async (targetCanvasId, targetDesignId) => {
    if (!endpointFactory) throw new Error('Archive is not available');
    const archiveAPI = endpointFactory(
      `designer/canvas/${encodeURIComponent(String(targetCanvasId))}/designs/${encodeURIComponent(String(targetDesignId))}/archive/`
    );
    return archiveAPI.create({});
  };

  const restoreDesign = async (targetCanvasId, targetDesignId) => {
    if (!endpointFactory) throw new Error('Restore is not available');
    const restoreAPI = endpointFactory(
      `designer/canvas/${encodeURIComponent(String(targetCanvasId))}/designs/${encodeURIComponent(String(targetDesignId))}/restore/`
    );
    return restoreAPI.create({});
  };

  return {
    saveCanvas,
    getCanvas,
    getCanvasTargetId: () => (canvasTargetResolved ? canvasId : ''),
    getCanvasDesignTargetId: () => canvasDesignTargetId,
    setCanvasTargetId: (id, nextDesignId) => {
      canvasId = normalizeId(String(id));
      canvasDesignTargetId = normalizeId(String(nextDesignId || id));
      explicitDesignSelection =
        nextDesignId != null && String(nextDesignId).trim() !== '';
      canvasTargetResolved = true;
    },
    setManualPersistOnly: (value) => {
      manualPersistOnly = Boolean(value);
    },
    listCanvasDesignsForLead,
    archiveCanvas,
    restoreCanvas,
    archiveDesign,
    restoreDesign
  };
};
